import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLeads } from '@/hooks/useLeads';
import { useLoading } from '@/hooks/useLoading';
import { supabase } from '@/lib/supabase';
import { SkeletonStat, SkeletonRow } from '@/components/Skeleton';
import BentoCard from '@/components/BentoCard';
import {
  TrendingUp,
  TrendingDown,
  Inbox,
  CheckCircle,
  FileSignature,
  AlertTriangle,
  ChevronRight,
  Sparkles,
  Activity,
  Phone,
  Users,
  Mail,
  MapPin,
  ArrowUpRight,
  Send,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';

interface StatTile {
  label: string;
  value: string;
  delta: string;
  deltaPositive: boolean;
  icon: React.ReactNode;
}

interface FlooringData {
  floor_type?: string;
  floor_subtype?: string;
  surface_area_m2?: number;
  rooms?: string[];
  has_underfloor_heating?: boolean;
  current_floor_removal_needed?: boolean;
  quality_preference?: string;
}

function getFlooringSummary(lead: any): string | null {
  const data = lead.intake_data as FlooringData | undefined;
  if (!data) return null;
  if (data.surface_area_m2 && data.floor_type) {
    return `${data.surface_area_m2}m² · ${data.floor_type}`;
  }
  return data.floor_type || null;
}

export default function PartnerDashboardPage() {
  const { user, effectivePartnerId } = useAuth();
  const navigate = useNavigate();
  const { leads: liveLeads } = useLeads(effectivePartnerId);

  const stats = useMemo(() => {
    if (!effectivePartnerId) return null;

    const now = new Date();
    const thisMonth = now.getMonth();
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const thisYear = now.getFullYear();
    const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

    const pLeads = [...liveLeads]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    const monthLeads = pLeads.filter(l => {
      const d = new Date(l.created_at);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    });
    const lastMonthLeads = pLeads.filter(l => {
      const d = new Date(l.created_at);
      return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
    });

    const total = pLeads.length;
    const newLeads = pLeads.filter(l => l.status === 'new').length;
    const contacted = pLeads.filter(l => l.status !== 'new').length;
    const offerteVerstuurd = pLeads.filter(l => l.status === 'offerte_verstuurd').length;
    const akkoordStatuses = ['akkoord', 'getekend', 'aanbetaling_gevraagd', 'aanbetaling_ontvangen'];
    const akkoord = pLeads.filter(l => akkoordStatuses.includes(l.status)).length;

    const lastMonthContacted = lastMonthLeads.filter(l => l.status !== 'new').length;
    const lastMonthOfferte = lastMonthLeads.filter(l => l.status === 'offerte_verstuurd').length;
    const lastMonthAkkoord = lastMonthLeads.filter(l => akkoordStatuses.includes(l.status)).length;

    const deltaPct = (curr: number, prev: number) =>
      prev > 0 ? Math.round(((curr - prev) / prev) * 100) : 0;

    const tiles: StatTile[] = [
      {
        label: 'Leads deze maand',
        value: String(monthLeads.length),
        delta: `${deltaPct(monthLeads.length, lastMonthLeads.length) >= 0 ? '+' : ''}${deltaPct(monthLeads.length, lastMonthLeads.length)}% vs vorige maand`,
        deltaPositive: deltaPct(monthLeads.length, lastMonthLeads.length) >= 0,
        icon: <Inbox size={18} strokeWidth={1.5} />,
      },
      {
        label: 'Gecontacteerd',
        value: String(contacted),
        delta: `${deltaPct(contacted, lastMonthContacted) >= 0 ? '+' : ''}${deltaPct(contacted, lastMonthContacted)}% vs vorige maand`,
        deltaPositive: deltaPct(contacted, lastMonthContacted) >= 0,
        icon: <CheckCircle size={18} strokeWidth={1.5} />,
      },
      {
        label: 'Offerte verstuurd',
        value: String(offerteVerstuurd),
        delta: `${deltaPct(offerteVerstuurd, lastMonthOfferte) >= 0 ? '+' : ''}${deltaPct(offerteVerstuurd, lastMonthOfferte)}% vs vorige maand`,
        deltaPositive: deltaPct(offerteVerstuurd, lastMonthOfferte) >= 0,
        icon: <FileSignature size={18} strokeWidth={1.5} />,
      },
      {
        label: 'Akkoord / Getekend',
        value: String(akkoord),
        delta: `${deltaPct(akkoord, lastMonthAkkoord) >= 0 ? '+' : ''}${deltaPct(akkoord, lastMonthAkkoord)}% vs vorige maand`,
        deltaPositive: deltaPct(akkoord, lastMonthAkkoord) >= 0,
        icon: <TrendingUp size={18} strokeWidth={1.5} />,
      },
    ];

    const pendingInvoices = 0;

    // Actionable leads: new + uncontacted >24h
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const actionable = pLeads.filter(l =>
      l.status === 'new' || (l.status !== 'new' && l.updated_at && new Date(l.updated_at).getTime() < oneDayAgo)
    ).slice(0, 5);

    // Alerts
    const alerts: { type: 'warning' | 'danger' | 'info'; message: string }[] = [];
    if (newLeads > 0) {
      alerts.push({
        type: 'warning',
        message: `${newLeads} nieuwe lead${newLeads === 1 ? '' : 's'} wacht${newLeads === 1 ? '' : 'en'} op contact`,
      });
    }
    if (pendingInvoices > 0) {
      alerts.push({
        type: 'info',
        message: `${pendingInvoices} openstaande factuur${pendingInvoices === 1 ? '' : 'en'} · wacht op betaling`,
      });
    }

    return {
      tiles,
      actionable,
      recentLeads: pLeads.slice(0, 5),
      newLeads,
      pendingInvoices,
      total,
      pLeads,
      alerts,
    };
  }, [effectivePartnerId, liveLeads]);

  const { loading } = useLoading(stats, 500);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [offerM2, setOfferM2] = useState<Record<string, string>>({});
  const [sendingOffer, setSendingOffer] = useState<string | null>(null);
  const [offerSent, setOfferSent] = useState<Set<string>>(new Set());
  const [offerError, setOfferError] = useState<Record<string, string>>({});

  const toggleCard = (key: string) => {
    setExpandedCard(prev => prev === key ? null : key);
  };

  const handleSendBrochureEmail = async (leadId: string, partnerId: string | null) => {
    if (!partnerId) return;
    const m2 = parseFloat(offerM2[leadId] || '0');
    setSendingOffer(leadId);
    setOfferError(prev => ({ ...prev, [leadId]: '' }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/semmy-lead-intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ lead_id: leadId, partner_id: partnerId, action: 'resend_email', surface_m2: m2 > 0 ? m2 : undefined }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setOfferSent(prev => new Set([...prev, leadId]));
      } else {
        setOfferError(prev => ({ ...prev, [leadId]: json.error || 'Verzenden mislukt' }));
      }
    } catch {
      setOfferError(prev => ({ ...prev, [leadId]: 'Netwerkfout' }));
    } finally {
      setSendingOffer(null);
    }
  };

  const inboxLeads = useMemo(() => {
    if (!effectivePartnerId) return [];
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    return liveLeads
      .filter(l =>
        !l.deleted_at &&
        (l.status === 'new' || (l.status !== 'new' && l.updated_at && new Date(l.updated_at).getTime() < oneDayAgo))
      )
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 5);
  }, [effectivePartnerId, liveLeads]);

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No partner data available</p>
      </div>
    );
  }

  const qualityColor = (score: number | null) => {
    if (!score) return { color: 'var(--text-muted)' };
    if (score >= 0.7) return { color: 'var(--accent)' };
    if (score >= 0.4) return { color: 'var(--warning-text)' };
    return { color: 'var(--danger-text)' };
  };

  return (
    <div className="space-y-10 relative max-w-6xl mx-auto">
      {/* ── Header ── */}
      <div className="flex items-center justify-between pt-2 animate-fade-up">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
            Dashboard
          </h1>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
            {stats.tiles[0].value} leads deze maand
            {stats.newLeads > 0 && (
              <> · <span style={{ color: 'var(--warning-text)' }}>{stats.newLeads} nieuw</span></>
            )}
            {stats.newLeads === 0 && stats.total > 0 && (
              <> · {stats.total} totaal</>
            )}
          </p>
        </div>
        <button
          onClick={() => {
            const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
            window.dispatchEvent(event);
          }}
          className="flex items-center gap-2 px-4 py-2 rounded-full text-xs transition-all duration-300"
          style={{
            background: 'var(--bg-surface-raised)',
            border: '1px solid var(--border-primary)',
            color: 'var(--text-tertiary)',
          }}
        >
          <Sparkles size={13} style={{ color: 'var(--accent)' }} />
          <span>Ask Nova</span>
          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded border" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-muted)' }}>⌘K</span>
        </button>
      </div>

      {/* ── Alerts ── */}
      {stats.alerts.length > 0 && (
        <div className="relative space-y-2.5 animate-fade-up stagger-1">
          {stats.alerts.map((alert, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-[13px] transition-all duration-300"
              style={{
                background: alert.type === 'danger' ? 'var(--danger-bg)' : alert.type === 'warning' ? 'var(--warning-bg)' : 'var(--bg-surface-raised)',
                color: alert.type === 'danger' ? 'var(--danger-text)' : alert.type === 'warning' ? 'var(--warning-text)' : 'var(--text-secondary)',
              }}
            >
              <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{
                background: alert.type === 'danger' ? 'var(--danger-dot)' : alert.type === 'warning' ? 'var(--warning-dot)' : 'var(--text-muted)',
              }} />
              <span className="font-medium">{alert.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Inbox / Actionable Leads — Card Feed ── */}
      <div className="animate-fade-up stagger-2 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Inbox size={14} strokeWidth={1.5} style={{ color: 'var(--text-muted)' }} />
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Inbox</span>
            {inboxLeads.length > 0 && (
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: 'var(--accent-dim)', color: 'var(--accent)' }}
              >
                {inboxLeads.length}
              </span>
            )}
          </div>
          <Link
            to="/partner/inbox"
            className="flex items-center gap-1 text-[11px] transition-colors"
            style={{ color: 'var(--text-muted)' }}
          >
            Alle bekijken
            <ChevronRight size={12} />
          </Link>
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-xl p-5 border animate-pulse" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full" style={{ background: 'var(--bg-input)' }} />
                  <div className="flex-1 space-y-2">
                    <div className="w-32 h-3 rounded" style={{ background: 'var(--bg-input)' }} />
                    <div className="w-48 h-2.5 rounded" style={{ background: 'var(--bg-input)' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : inboxLeads.length > 0 ? (
          inboxLeads.map(lead => {
            const isExpanded = expandedCard === lead.id;
            const isNew = lead.status === 'new';
            const floorSummary = getFlooringSummary(lead);

            const sent = offerSent.has(lead.id);
            const sending = sendingOffer === lead.id;
            const err = offerError[lead.id];

            const detailPanel = (
              <div className="space-y-3 pt-2">
                {/* Contact row */}
                <div className="flex items-center gap-2 flex-wrap">
                  {lead.phone && (
                    <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border"
                      style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}
                    >
                      <Phone size={11} /> {lead.phone}
                    </a>
                  )}
                  {lead.email && (
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border"
                      style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}
                    >
                      <Mail size={11} /> {lead.email}
                    </a>
                  )}
                  {(lead.city || lead.postcode) && (
                    <span className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border"
                      style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}
                    >
                      <MapPin size={11} /> {[lead.postcode, lead.city].filter(Boolean).join(' ')}
                    </span>
                  )}
                  {(lead.fraud_signals?.length || 0) > 0 && (
                    <span className="flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-lg border"
                      style={{ color: 'var(--danger-text)', borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' }}
                    >
                      <AlertTriangle size={11} />
                      {lead.fraud_signals!.length} fraudsignaal{lead.fraud_signals!.length > 1 ? 'en' : ''}
                    </span>
                  )}
                </div>

                {/* Quick send brochure email */}
                {sent ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium"
                    style={{ background: 'var(--success-bg)', color: 'var(--success-text)', border: '1px solid var(--success-border)' }}
                  >
                    <CheckCircle size={13} /> Brochure e-mail verstuurd naar {lead.email}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="aura-input-wrap flex-1" style={{ borderRadius: '10px', maxWidth: 110 }}>
                      <div className="aura-input-inner" style={{ borderRadius: '9px', paddingLeft: 10, paddingRight: 8 }}>
                        <input
                          type="number"
                          placeholder="m²"
                          min={1}
                          value={offerM2[lead.id] || ''}
                          onChange={e => setOfferM2(prev => ({ ...prev, [lead.id]: e.target.value }))}
                          onClick={e => e.stopPropagation()}
                          className="flex-1 py-2 text-xs outline-none bg-transparent w-full"
                          style={{ color: 'var(--text-primary)' }}
                        />
                        <span className="text-xs shrink-0" style={{ color: 'var(--text-muted)' }}>m²</span>
                      </div>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); handleSendBrochureEmail(lead.id, effectivePartnerId); }}
                      disabled={sending || !lead.email}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-all disabled:opacity-50"
                      style={{ background: 'var(--text-primary)', color: 'var(--bg-surface)', border: 'none' }}
                      title={!lead.email ? 'Geen e-mailadres bekend' : 'Stuur brochure + prijzen voor alle vloertypes'}
                    >
                      {sending ? (
                        <div className="w-3 h-3 rounded-full border border-current border-t-transparent animate-spin" />
                      ) : (
                        <Send size={12} />
                      )}
                      Stuur brochure
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/partner/leads/${lead.id}`); }}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border"
                      style={{ color: 'var(--accent)', borderColor: 'var(--accent)', background: 'transparent' }}
                    >
                      <ArrowUpRight size={12} /> Lead
                    </button>
                  </div>
                )}
                {err && <p className="text-[11px]" style={{ color: 'var(--danger-text)' }}>{err}</p>}
              </div>
            );

            return (
              <BentoCard
                key={lead.id}
                size="full"
                expandable
                expanded={isExpanded}
                onToggle={() => toggleCard(lead.id)}
                active={isExpanded}
                className="row-hover"
                header={
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{
                        background: lead.routing_status === 'unmatched'
                          ? 'var(--danger-dot)'
                          : isNew
                            ? 'var(--warning-dot)'
                            : 'var(--success-dot)',
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {lead.firstname} {lead.lastname}
                        </h3>
                        {lead.city && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{lead.city}</span>}
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize"
                          style={{
                            borderColor: isNew ? 'var(--warning-border)' : 'var(--danger-border)',
                            background: isNew ? 'var(--warning-bg)' : 'var(--danger-bg)',
                            color: isNew ? 'var(--warning-text)' : 'var(--danger-text)',
                          }}
                        >
                          {isNew ? 'Nieuw · nog contact opnemen' : 'Stilgevallen · opvolgen'}
                        </span>
                        {floorSummary && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border"
                            style={{ background: 'var(--accent-dim)', color: 'var(--accent)', borderColor: 'var(--accent)' }}
                          >
                            <i className="ri-stack-line mr-0.5" />
                            {floorSummary}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {lead.phone && (
                          <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                            <Phone size={10} />
                            {lead.phone}
                          </span>
                        )}
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{formatDistanceToNow(new Date(lead.created_at), { addSuffix: true, locale: nl })}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-xs font-semibold"
                        style={{
                          color: (lead.quality_score || 0) >= 0.7 ? 'var(--text-primary)' :
                            (lead.quality_score || 0) >= 0.4 ? 'var(--warning-text)' : 'var(--danger-text)',
                        }}
                      >
                        {(() => {
                          const s = lead.quality_score;
                          if (!s) return '—';
                          if (s >= 0.7) return 'Hoog';
                          if (s >= 0.4) return 'Gemiddeld';
                          return 'Laag';
                        })()}
                      </span>
                    </div>
                  </div>
                }
                footer={detailPanel}
              >
                {null}
              </BentoCard>
            );
          })
        ) : (
          <BentoCard size="full">
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <CheckCircle size={18} strokeWidth={1.5} style={{ color: 'var(--success-text)' }} />
              <span className="text-[13px]" style={{ color: 'var(--text-muted)' }}>Alle leads zijn behandeld. Niets te beoordelen.</span>
            </div>
          </BentoCard>
        )}
      </div>

      {/* ── Stats Grid — 6 bento tiles ── */}
      {loading ? (
        <div className="bento-grid animate-fade-up stagger-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonStat key={i} />
          ))}
        </div>
      ) : (
        <div className="bento-grid animate-fade-up stagger-3">
          {stats.tiles.map((tile, i) => {
            const key = tile.label;
            const isExpanded = expandedCard === key;

            const detailContent = (() => {
              if (key === 'Leads deze maand') {
                const newCount = stats.pLeads.filter(l => l.status === 'new' && new Date(l.created_at).getMonth() === new Date().getMonth()).length;
                return (
                  <div className="space-y-2 pt-2">
                    <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Verdeling</p>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] px-2 py-0.5 rounded border"
                        style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}
                      >Nieuw: {newCount}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded border"
                        style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}
                      >In behandeling: {stats.pLeads.filter(l => l.status !== 'new' && l.status !== 'completed').length}</span>
                    </div>
                  </div>
                );
              }
              if (key === 'Gecontacteerd') {
                const contacted = stats.pLeads.filter(l => l.status !== 'new').length;
                return (
                  <div className="space-y-2 pt-2">
                    <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Overzicht</p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{contacted} van {stats.pLeads.length} leads zijn gecontacteerd</p>
                  </div>
                );
              }
              if (key === 'Offerte verstuurd') {
                const recentSigned = stats.pLeads
                  .filter(l => l.status === 'offerte_verstuurd')
                  .sort((a, b) => new Date(b.signed_at || b.created_at).getTime() - new Date(a.signed_at || a.created_at).getTime())
                  .slice(0, 3);
                return (
                  <div className="space-y-2 pt-2">
                    <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Recent verstuurd</p>
                    {recentSigned.map(l => (
                      <div key={l.id} className="flex items-center justify-between">
                        <span className="text-xs truncate max-w-[160px]" style={{ color: 'var(--text-secondary)' }}>{l.firstname} {l.lastname}</span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                          {l.signed_at ? formatDistanceToNow(new Date(l.signed_at!), { addSuffix: true, locale: nl }) : '—'}
                        </span>
                      </div>
                    ))}
                    {recentSigned.length === 0 && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Nog geen offertes verstuurd</p>}
                  </div>
                );
              }
              if (key === 'Akkoord / Getekend') {
                const urgent = stats.pLeads.filter(l => l.status === 'new').slice(0, 3);
                return (
                  <div className="space-y-2 pt-2">
                    <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Wacht op jou</p>
                    {urgent.map(l => (
                      <div key={l.id} className="flex items-center justify-between">
                        <span className="text-xs truncate max-w-[160px]" style={{ color: 'var(--text-secondary)' }}>{l.firstname} {l.lastname}</span>
                        <Link to={`/partner/leads/${l.id}`} className="text-[10px] hover:underline" style={{ color: 'var(--accent)' }}>Open</Link>
                      </div>
                    ))}
                    {urgent.length === 0 && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Alles bijgewerkt</p>}
                  </div>
                );
              }
              return null;
            })();

            return (
              <BentoCard
                key={i}
                size="sm"
                glow={false}
                active={isExpanded}
                expandable={!!detailContent}
                expanded={isExpanded}
                onToggle={() => toggleCard(key)}
                header={
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center"
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-muted)' }}
                    >
                      {tile.icon}
                    </div>
                    <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{tile.label}</span>
                  </div>
                }
                footer={detailContent || undefined}
              >
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>{tile.value}</p>
                    <div className="flex items-center gap-1 mt-2 text-[11px] font-medium"
                      style={{ color: tile.deltaPositive ? 'var(--accent)' : 'var(--danger-text)' }}
                    >
                      {tile.deltaPositive ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                      <span>{tile.delta}</span>
                    </div>
                  </div>
                </div>
              </BentoCard>
            );
          })}
        </div>
      )}

      {/* ── Detail Panels — 1 column ── */}
      <div className="bento-grid animate-fade-up stagger-4">
        {/* Recent Leads Quality */}
        <BentoCard
          size="md"
          header={
            <div className="flex items-center justify-between w-full">
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Recente kwaliteit</span>
              <Users size={14} style={{ color: 'var(--text-muted)' }} />
            </div>
          }
        >
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--bg-input)' }} />
                  <div className="flex-1 space-y-1.5">
                    <div className="w-32 h-3 rounded animate-pulse" style={{ background: 'var(--bg-input)' }} />
                    <div className="w-20 h-2.5 rounded animate-pulse" style={{ background: 'var(--bg-input)' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              {stats.recentLeads.slice(0, 5).map(lead => {
                const floorSummary = getFlooringSummary(lead);
                return (
                  <div key={lead.id} className="flex items-center gap-3 py-2.5 group cursor-pointer rounded-lg transition-colors hover:bg-[var(--bg-surface-raised)]"
                    onClick={() => navigate(`/partner/leads/${lead.id}`)}
                  >
                    <div className="w-2 h-2 rounded-full shrink-0"
                      style={{
                        background: (lead.quality_score || 0) >= 0.7 ? 'var(--success-dot)' : (lead.quality_score || 0) >= 0.4 ? 'var(--warning-dot)' : 'var(--danger-dot)',
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium truncate" style={{ color: 'var(--text-secondary)' }}>{lead.firstname} {lead.lastname}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {floorSummary && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded border"
                            style={{ background: 'var(--accent-dim)', color: 'var(--accent)', borderColor: 'var(--accent)' }}
                          >
                            {floorSummary}
                          </span>
                        )}
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                          {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true, locale: nl })}
                        </span>
                      </div>
                    </div>
                    <span className="text-[13px] font-semibold shrink-0" style={qualityColor(lead.quality_score)}>
                      {lead.quality_score?.toFixed(2) || '—'}
                    </span>
                  </div>
                );
              })}
              {stats.recentLeads.length === 0 && (
                <p className="text-sm text-center py-4" style={{ color: 'var(--text-tertiary)' }}>Nog geen leads</p>
              )}
            </div>
          )}
        </BentoCard>
      </div>

      {/* ── Activity Feed ── */}
      <div className="animate-fade-up stagger-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity size={14} strokeWidth={1.5} style={{ color: 'var(--text-muted)' }} />
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Activiteit</span>
          </div>
        </div>
        <BentoCard
          size="full"
          header={
            <div className="flex items-center w-full">
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Recente activiteit</span>
            </div>
          }
        >
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: 'var(--border-separator)' }}>
              {stats.recentLeads.slice(0, 6).map((lead) => (
                <div key={lead.id} className="flex items-start gap-3 py-3 px-1">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{ background: 'var(--bg-input)', color: 'var(--text-muted)', border: '1px solid var(--border-primary)' }}
                  >
                    <Inbox size={13} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                      Lead ontvangen: {lead.firstname} {lead.lastname}
                    </p>
                    <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true, locale: nl })}
                    </p>
                  </div>
                </div>
              ))}
              {stats.recentLeads.length === 0 && (
                <div className="px-5 py-8 text-center">
                  <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Geen recente activiteit</p>
                </div>
              )}
            </div>
          )}
        </BentoCard>
      </div>
    </div>
  );
}
